import { objectEntries, valueof } from 'utils/typeUtils';

import type { DefaultTheme } from 'styled-components';

const color = {
  common: { white: '#fff', black: '#000', transparent: 'transparent', splatBlue: '#1ABAFF' },
  storm: {
    25: '#F3F9FF',
    50: '#E1EDFF',
    100: '#BDD4EE',
    200: '#9EB5D4',
    300: '#7D98BA',
    400: '#6482A6',
    500: '#4B6D93',
    600: '#3D5F82',
    700: '#2E4C6C',
    800: '#1F3A56',
    850: '#112B47',
    900: '#0B263E',
  },
  lightning: {
    25: '#FFFEF0',
    50: '#FFFDE6',
    100: '#FEF8C2',
    200: '#FDF39A',
    300: '#FDEE72',
    400: '#FBE951',
    500: '#F8E42F',
    600: '#FAD32D',
    700: '#F8BC25',
    800: '#F6A41D',
    850: '#F6A41D',
    900: '#F37B0E',
  },
  highSeas: {
    25: '#F2FBFC',
    50: '#E0F7FA',
    100: '#B2EBF3',
    200: '#7FDEEB',
    300: '#4BD0E3',
    400: '#21C6DC',
    500: '#00BCD6',
    600: '#00ACC3',
    700: '#0097A9',
    800: '#008391',
    850: '#008391',
    900: '#006065',
  },
  wave: {
    25: '#F0FAFE',
    50: '#E2F7FF',
    100: '#B7EBFD',
    200: '#88DFFB',
    300: '#5BD1F6',
    400: '#3FC8F0',
    500: '#35BEEA',
    600: '#2EAED6',
    700: '#2399BA',
    800: '#008391',
    850: '#1985A1',
    900: '#036374',
  },
  bubbles: {
    25: '#E6F2FA',
    50: '#CDE6F5',
    100: '#88BEDF',
    200: '#3493C2',
    300: '#006CA7',
    400: '#005498',
    500: '#003D86',
    600: '#00347B',
    700: '#00276B',
    800: '#001A58',
    850: '#001A58',
    900: '#000043',
  },
  foam: {
    25: '#FAFCFE',
    50: '#F3F9FF',
    100: '#E4F0F9',
    200: '#D6E6F2',
    300: '#C4DAEA',
    400: '#B4CDE0',
    500: '#A4C2D8',
    600: '#94B0C3',
    700: '#8198A9',
    800: '#718491',
    850: '#718491',
    900: '#5D6C77',
  },
  coral: {
    25: '#FFF9F5',
    50: '#FAE8E8',
    100: '#FDCABE',
    200: '#FCA795',
    300: '#FC846C',
    400: '#FC684D',
    500: '#FD4B31',
    600: '#F2452D',
    700: '#E43E28',
    800: '#D53723',
    850: '#D53723',
    900: '#BC2A1B',
  },
  milkTea: {
    25: '#FCF5F3',
    50: '#F9EBE6',
    100: '#F5D0C5',
    200: '#F5B19C',
    300: '#F89171',
    400: '#F97850',
    500: '#FB6132',
    600: '#F05B2E',
    700: '#E25429',
    800: '#D44D25',
    850: '#D44D25',
    900: '#BB401C',
  },
  cloud: {
    25: '#FCFCFD',
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EFEFEF',
    300: '#E1E1E1',
    400: '#BFBFBF',
    500: '#A0A0A0',
    600: '#777777',
    700: '#626262',
    800: '#434343',
    850: '#434343',
    900: '#222222',
  },
  lychee: {
    25: '#FDF2F5',
    50: '#FAE4EA',
    100: '#F4BBCC',
    200: '#EC91AA',
    300: '#E5678A',
    400: '#DE4B72',
    500: '#D8365D',
    600: '#C83259',
    700: '#B22E54',
    800: '#9D294F',
    850: '#9D294F',
    900: '#782346',
  },
  seastar: {
    25: '#F8F2F8',
    50: '#EFE2F0',
    100: '#D8B7DC',
    200: '#BF88C5',
    300: '#A55BAE',
    400: '#92399D',
    500: '#80168D',
    600: '#741388',
    700: '#630E80',
    800: '#530A78',
    850: '#530A78',
    900: '#360568',
  },
  taro: {
    25: '#FBF7FD',
    50: '#F1E3F9',
    100: '#DCB8F1',
    200: '#C589E8',
    300: '#AC5ADB',
    400: '#9837CF',
    500: '#850BC2',
    600: '#740DBC',
    700: '#5D0DB4',
    800: '#460DAC',
    850: '#460DAC',
    900: '#000D9E',
  },
  algae: {
    25: '#F7FDFA',
    50: '#E6FAF0',
    100: '#C3F2D9',
    200: '#9AE9C1',
    300: '#69E1A7',
    400: '#37D992',
    500: '#00D07F',
    600: '#00C073',
    700: '#00AC65',
    800: '#009A59',
    850: '#009A59',
    900: '#007942',
  },
  error: {
    25: '#FFFBFA',
    50: '#FEF3F2',
    100: '#FEE4E2',
    200: '#FECDCA',
    300: '#FDA29B',
    400: '#F97066',
    500: '#F04438',
    600: '#D92D20',
    700: '#B42318',
    800: '#912018',
    850: '#912018',
    900: '#7A271A',
  },
  warning: {
    25: '#FFFCF5',
    50: '#FFFAEB',
    100: '#FEF0C7',
    200: '#FEDF89',
    300: '#FEC84B',
    400: '#FDB022',
    500: '#F79009',
    600: '#DC6803',
    700: '#B54708',
    800: '#93370D',
    850: '#93370D',
    900: '#7A2E0E',
  },
  success: {
    25: '#F6FEF9',
    50: '#ECFDF3',
    100: '#D1FADF',
    200: '#A6F4C5',
    300: '#6CE9A6',
    400: '#32D583',
    500: '#12B76A',
    600: '#039855',
    700: '#027A48',
    800: '#05603A',
    850: '#05603A',
    900: '#054F31',
  },
} as const;

export const globalValues = ['inherit', 'initial', 'revert', 'revert-layer', 'unset'] as const;
export type GlobalValues = typeof globalValues[number];

/* TODO: Add gradients */
export const gradient = {
  Grey: 'linear-gradient(90deg, #475467 0%, #667085 100%);',
} as const;

export type GradientStyles = keyof typeof gradient;
type GetGradientList<T extends string> = `gradient-${T}`;
export type GradientList = GetGradientList<GradientStyles>;

export type ColorObject = typeof color;

type GetColorOptions<T> = {
  readonly [P in keyof T]: keyof T[P];
};

export type ColorOptions = GetColorOptions<ColorObject>;

type GetColorList<T> = {
  readonly [P in keyof T]: `${P extends number | string ? P : never}-${keyof T[P] extends string | number
    ? keyof T[P]
    : never}`;
}[keyof T];

export type ColorList = GetColorList<ColorObject>;
export type ColorDefinition = keyof ColorObject;
export const colorListArray = objectEntries(color)
  .map(([colorName, colorValues]) => Object.keys(colorValues).map(value => `${colorName}-${value}`))
  .flat() as ColorList[];

export interface LightAndDark {
  light: ColorList;
  dark: ColorList;
}

type Common = ColorOptions['common'];
type Others = valueof<Omit<ColorOptions, 'common'>>;
type ColorArray = [ColorDefinition, Common | Others];

export type GetColorDefinition = ColorList | LightAndDark;

export const getColor = (palette: DefaultTheme['palette'], colors: GetColorDefinition) => {
  if (!colors) {
    return;
  }
  const [colorKey, colorValue] =
    typeof colors === 'string' ? (colors.split('-') as ColorArray) : (colors[palette.mode].split('-') as ColorArray);
  const subPalette = palette[colorKey];

  return 'white' in subPalette ? subPalette[colorValue as Common] : subPalette[colorValue as Others];
};

export default color;
